import { useEffect, useRef } from "react";

/**
 * Lightweight canvas particles — floating light dots + subtle connectors.
 * Zero dependencies, GPU-friendly, adapts to viewport.
 */
export default function Particles({ density = 60, colorA = "#00F0FF", colorB = "#7000FF" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let running = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
    }
    resize();
    window.addEventListener("resize", resize);

    const rand = (a, b) => a + Math.random() * (b - a);
    const particles = Array.from({ length: density }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: rand(-0.15, 0.15) * dpr,
      vy: rand(-0.15, 0.15) * dpr,
      r: rand(0.6, 2.2) * dpr,
      hue: Math.random() > 0.5 ? colorA : colorB,
      a: rand(0.35, 0.9),
    }));

    function tick() {
      if (!running) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // draw connectors
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130 * dpr) {
            ctx.strokeStyle = `rgba(0, 240, 255, ${(1 - dist / (130 * dpr)) * 0.08})`;
            ctx.lineWidth = 0.6 * dpr;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }

      // draw dots
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.hue;
        ctx.globalAlpha = p.a;
        ctx.shadowBlur = 12 * dpr;
        ctx.shadowColor = p.hue;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      }

      raf = requestAnimationFrame(tick);
    }
    tick();

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [density, colorA, colorB]);

  return <canvas ref={canvasRef} className="particles-canvas" aria-hidden />;
}
